import { useState, useEffect } from 'react';

const STORAGE_KEY = 'app_timezones_cache';
const CACHE_VERSION = '1.0';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

interface CachedData {
  timezones: string[];
  version: string;
  timestamp: number;
}

// In-memory fallback cache
let memoryCache: string[] | null = null;

/**
 * Timezone caching hook with progressive fallback:
 * 1. localStorage (persistent across sessions)
 * 2. sessionStorage (persistent for session)
 * 3. in-memory (persistent for page lifecycle)
 */
export const useTimezoneCache = () => {
  const [timezones, setTimezones] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTimezones();
  }, []);

  const loadTimezones = async () => {
    try {
      // Step 1: Check memory cache first (fastest)
      if (memoryCache && memoryCache.length > 0) {
        setTimezones(memoryCache);
        setLoading(false);
        return;
      }

      // Step 2: Try localStorage
      const cachedData = tryGetFromStorage('localStorage');
      if (cachedData) {
        memoryCache = cachedData;
        setTimezones(cachedData);
        setLoading(false);
        return;
      }

      // Step 3: Try sessionStorage
      const sessionData = tryGetFromStorage('sessionStorage');
      if (sessionData) {
        memoryCache = sessionData;
        setTimezones(sessionData);
        setLoading(false);
        return;
      }

      // Step 4: Fetch from database (only if all caches miss)
      await fetchAndCacheTimezones();
    } catch (err) {
      console.error('Failed to load timezones:', err);
      setError('Failed to load timezone data');
      setLoading(false);
    }
  };

  const tryGetFromStorage = (storageType: 'localStorage' | 'sessionStorage'): string[] | null => {
    try {
      const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
      const cached = storage.getItem(STORAGE_KEY);
      
      if (!cached) return null;

      const data: CachedData = JSON.parse(cached);
      
      // Validate cache version and age
      const isValid = 
        data.version === CACHE_VERSION &&
        data.timezones &&
        Array.isArray(data.timezones) &&
        data.timezones.length > 0 &&
        (Date.now() - data.timestamp) < CACHE_DURATION;

      if (isValid) {
        return data.timezones;
      }

      // Clear invalid cache
      storage.removeItem(STORAGE_KEY);
      return null;
    } catch (err) {
      // Storage not available or quota exceeded
      return null;
    }
  };

  const fetchAndCacheTimezones = async () => {
    // Use a predefined list of common timezones instead of querying pg_timezone_names
    // This eliminates the expensive database query entirely
    const commonTimezones = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Phoenix',
      'America/Anchorage',
      'Pacific/Honolulu',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Europe/Rome',
      'Europe/Madrid',
      'Europe/Amsterdam',
      'Europe/Brussels',
      'Europe/Vienna',
      'Europe/Stockholm',
      'Europe/Copenhagen',
      'Europe/Helsinki',
      'Europe/Oslo',
      'Europe/Warsaw',
      'Europe/Prague',
      'Europe/Budapest',
      'Europe/Bucharest',
      'Europe/Athens',
      'Europe/Istanbul',
      'Europe/Moscow',
      'Asia/Dubai',
      'Asia/Kolkata',
      'Asia/Bangkok',
      'Asia/Singapore',
      'Asia/Hong_Kong',
      'Asia/Shanghai',
      'Asia/Tokyo',
      'Asia/Seoul',
      'Australia/Sydney',
      'Australia/Melbourne',
      'Australia/Brisbane',
      'Australia/Perth',
      'Pacific/Auckland',
      'America/Toronto',
      'America/Vancouver',
      'America/Mexico_City',
      'America/Sao_Paulo',
      'America/Argentina/Buenos_Aires',
      'Africa/Johannesburg',
      'Africa/Cairo',
      'Africa/Lagos',
    ].sort();

    // Store in memory cache
    memoryCache = commonTimezones;
    setTimezones(commonTimezones);

    // Try to persist to localStorage
    trySaveToStorage('localStorage', commonTimezones);

    // Try to persist to sessionStorage as backup
    trySaveToStorage('sessionStorage', commonTimezones);

    setLoading(false);
  };

  const trySaveToStorage = (storageType: 'localStorage' | 'sessionStorage', timezones: string[]) => {
    try {
      const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
      const data: CachedData = {
        timezones,
        version: CACHE_VERSION,
        timestamp: Date.now(),
      };
      storage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      // Silently fail if storage is not available
      // In-memory cache will still work
    }
  };

  const clearCache = () => {
    memoryCache = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore storage errors */ }
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore storage errors */ }
  };

  return {
    timezones,
    loading,
    error,
    clearCache,
  };
};
